import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { IcX } from './Icons';

const CUR_YEAR = new Date().getFullYear();
const SESSIONS = Array.from({ length: 5 }, (_, i) => {
  const y = CUR_YEAR - i - 1;
  return `${y}/${y + 1}`;
});
const SEMESTERS = ['First', 'Second'];

const PROGRAM_LABELS = { utme: 'UTME', putme: 'Post-UTME', both: 'UTME & Post-UTME' };

function scoreColor(pct) {
  if (pct >= 70) return '#16a34a';
  if (pct >= 50) return '#d97706';
  return '#dc2626';
}
function scoreLabel(pct) {
  if (pct >= 70) return 'Excellent';
  if (pct >= 60) return 'Good';
  if (pct >= 50) return 'Average';
  return 'Below Average';
}

function AnimBar({ pct, delay = 0 }) {
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(pct), 120 + delay); return () => clearTimeout(t); }, [pct, delay]);
  return (
    <div style={{ height: 7, background: '#EAECF0', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${w}%`, background: scoreColor(pct), borderRadius: 99, transition: 'width 1s cubic-bezier(0.16,1,0.3,1)' }} />
    </div>
  );
}

/* ── Canvas image download ── */
function downloadImage(data, quizScores, assessmentResults, certificates, session, semester) {
  const s    = data.student;
  const name = `${s.lastName || ''} ${s.firstName || ''}`.trim();
  const date = new Date().toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' });

  const SCALE = 2, W = 640, PADX = 44;
  const HEADER = 128, INFO_H = 76, ROW_H = 40, SEC_H = 38;
  const qR = quizScores.length, cR = assessmentResults.length, certR = certificates.length;
  const secs = (qR > 0 ? 1 : 0) + (cR > 0 ? 1 : 0) + (certR > 0 ? 1 : 0);
  const H = HEADER + INFO_H + 28 + secs * SEC_H + (qR + cR + certR) * ROW_H + 72;

  const canvas = document.createElement('canvas');
  canvas.width = W * SCALE; canvas.height = H * SCALE;
  const ctx = canvas.getContext('2d');
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H);

  const hg = ctx.createLinearGradient(0, 0, W, HEADER);
  hg.addColorStop(0, '#0F172A'); hg.addColorStop(1, '#1e293b');
  ctx.fillStyle = hg; ctx.fillRect(0, 0, W, HEADER);

  const orb = ctx.createRadialGradient(W - 40, 20, 0, W - 40, 20, 130);
  orb.addColorStop(0, 'rgba(255,165,0,0.1)'); orb.addColorStop(1, 'rgba(255,165,0,0)');
  ctx.fillStyle = orb; ctx.fillRect(0, 0, W, HEADER);

  ctx.fillStyle = '#FFA500'; ctx.fillRect(0, HEADER - 3, W, 3);

  ctx.fillStyle = '#FFA500'; ctx.font = `700 9px Arial`; ctx.letterSpacing = '3px';
  ctx.fillText('TOPPERS TUTORIAL · UNIVERSITY OF IBADAN', PADX, 28);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = '#fff'; ctx.font = `800 26px Arial`;
  ctx.fillText(name || 'Unknown Student', PADX, 66);

  ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.font = `400 11px Arial`;
  ctx.fillText(`${s.tutorialId || '—'}   ·   ${s.department || '—'}`, PADX, 88);

  ctx.fillStyle = 'rgba(255,165,0,0.85)'; ctx.font = `500 10px Arial`;
  const ctxLine = semester ? `${session} · ${semester} Semester` : session;
  ctx.fillText(`${ctxLine} · Verified ${date}`, PADX, 108);

  let y = HEADER + 22;

  // Info row
  ctx.fillStyle = '#F8FAFC';
  try { ctx.roundRect(PADX - 12, y - 10, W - (PADX - 12) * 2, INFO_H, 8); ctx.fill(); }
  catch { ctx.fillRect(PADX - 12, y - 10, W - (PADX - 12) * 2, INFO_H); }

  const infos = semester
    ? [['Faculty', s.faculty || '—'], ['Semester', `${semester} Semester`], ['Session', session]]
    : [['University', s.faculty || '—'], ['Course', s.department || '—'], ['Programme', session]];
  infos.forEach(([lbl, val], i) => {
    const x = PADX + i * 185;
    ctx.fillStyle = '#94A3B8'; ctx.font = `700 8px Arial`; ctx.letterSpacing = '0.5px';
    ctx.fillText(lbl.toUpperCase(), x, y + 12); ctx.letterSpacing = '0px';
    ctx.fillStyle = '#0F172A'; ctx.font = `700 13px Arial`;
    ctx.fillText(val, x, y + 32);
  });
  y += INFO_H + 22;

  const drawSection = (title, rows, renderRow) => {
    if (!rows.length) return;
    ctx.fillStyle = '#0F172A'; ctx.font = `700 9px Arial`; ctx.letterSpacing = '0.5px';
    ctx.fillText(title.toUpperCase(), PADX, y + 12); ctx.letterSpacing = '0px';
    ctx.fillStyle = '#FFA500'; ctx.fillRect(PADX, y + 18, 28, 2);
    y += SEC_H;
    rows.forEach((row, ri) => {
      if (ri % 2 === 0) { ctx.fillStyle = '#F8FAFC'; ctx.fillRect(PADX - 12, y - 8, W - (PADX - 12) * 2, ROW_H - 4); }
      renderRow(row, y);
      y += ROW_H;
    });
    y += 8;
  };

  drawSection('Quiz Performance', quizScores, (q, ry) => {
    const pct = q.avgAccuracy || 0;
    ctx.fillStyle = '#374151'; ctx.font = `600 12px Arial`;
    ctx.fillText(q.course, PADX, ry + 9);
    ctx.fillStyle = '#9CA3AF'; ctx.font = `400 9px Arial`;
    ctx.fillText(`${q.attempts} attempt${q.attempts !== 1 ? 's' : ''}`, PADX, ry + 22);
    const BX = PADX + 108, BW = 270, BY = ry + 4;
    ctx.fillStyle = '#E9EBF0';
    try { ctx.beginPath(); ctx.roundRect(BX, BY, BW, 6, 3); ctx.fill(); }
    catch { ctx.fillRect(BX, BY, BW, 6); }
    ctx.fillStyle = scoreColor(pct);
    try { ctx.beginPath(); ctx.roundRect(BX, BY, BW * pct / 100, 6, 3); ctx.fill(); }
    catch { ctx.fillRect(BX, BY, BW * pct / 100, 6); }
    ctx.fillStyle = scoreColor(pct); ctx.font = `700 13px Arial`;
    ctx.fillText(`${pct}%`, BX + BW + 10, ry + 13);
  });

  drawSection('CA Results', assessmentResults, (r, ry) => {
    const pct = parseFloat(r.percentage) || 0;
    ctx.fillStyle = '#374151'; ctx.font = `600 12px Arial`;
    ctx.fillText(r.week || 'Assessment', PADX, ry + 9);
    ctx.fillStyle = '#9CA3AF'; ctx.font = `400 9px Arial`;
    ctx.fillText(r.course, PADX, ry + 22);
    ctx.fillStyle = scoreColor(pct); ctx.font = `700 14px Arial`;
    ctx.fillText(`${pct}%`, W - PADX - 42, ry + 14);
  });

  drawSection('Certificates Earned', certificates, (c, ry) => {
    ctx.fillStyle = '#14532D'; ctx.font = `700 12px Arial`;
    ctx.fillText(c.course || c.title, PADX, ry + 9);
    ctx.fillStyle = '#16a34a'; ctx.font = `400 9px Arial`;
    ctx.fillText(`${c.semester} Semester · ${c.year}`, PADX, ry + 22);
    ctx.fillStyle = '#16a34a'; ctx.font = `700 14px Arial`;
    ctx.fillText(`${c.grade}%`, W - PADX - 42, ry + 14);
  });

  ctx.fillStyle = '#CBD5E1'; ctx.font = `400 9px Arial`;
  ctx.textAlign = 'center';
  ctx.fillText('Verified by Toppers Tutorial · University of Ibadan · topperstutorial.com.ng', W / 2, H - 18);

  const a = document.createElement('a');
  a.download = `${(s.lastName || 'result').replace(/\s+/g, '-').toLowerCase()}-${session.replace('/', '-')}-${semester}.png`;
  a.href = canvas.toDataURL('image/png');
  a.click();
}

/* ── Print (PDF) ── */
function printCard(cardId) {
  const card = document.getElementById(cardId);
  if (!card) return;
  const style = document.createElement('style');
  style.id = '_rcm_print';
  style.textContent = `@media print{body>*{display:none!important}#_rcm_portal{display:block!important;font-family:system-ui,sans-serif}@page{margin:1.2cm}}`;
  const portal = document.createElement('div');
  portal.id = '_rcm_portal';
  portal.style.cssText = 'display:none;position:fixed;inset:0;background:#fff;z-index:99999;padding:24px;overflow:auto';
  portal.appendChild(card.cloneNode(true));
  document.head.appendChild(style);
  document.body.appendChild(portal);
  window.print();
  document.head.removeChild(style);
  document.body.removeChild(portal);
}

export default function ResultCheckerModal({ onClose, currentSession }) {
  // Default session to the admin-configured current session
  const defaultSession = currentSession || SESSIONS[0];

  const [studentId, setStudentId] = useState('');
  const [surname,   setSurname]   = useState('');
  const [session,   setSession]   = useState(defaultSession);
  const [semester,  setSemester]  = useState(SEMESTERS[0]);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [err,       setErr]       = useState('');
  const [errors,    setErrors]    = useState({});
  const [course,    setCourse]    = useState('');
  const cardRef = useRef();

  // Keep default session in sync if prop changes
  useEffect(() => {
    if (currentSession && !data) setSession(currentSession);
  }, [currentSession]);

  // Derive enrollment year from selected session (e.g. "2025/2026" → 2026)
  const enrollYear = session ? session.split('/')[1] : String(CUR_YEAR);
  // JAMB / PUTME students use a TPJ tutorial ID — detect it to switch modes
  const isJambId = studentId.trim().toUpperCase().startsWith('TPJ');
  const idPlaceholder = isJambId ? `TPJ/${CUR_YEAR}/A0001` : `TT/${enrollYear}/A0001`;

  const validate = () => {
    const e = {};
    const id = studentId.trim().toUpperCase();
    if (!id) e.studentId = 'Required';
    else if (isJambId) {
      if (!/^TPJ\/\d{4}\/[A-Z0-9]+$/.test(id)) e.studentId = `Format: TPJ/${CUR_YEAR}/XXXX`;
    } else if (!/^TT\/\d{4}\/[A-Z0-9]+$/.test(id)) {
      e.studentId = `Format: TT/${enrollYear}/XXXX`;
    }
    if (!surname.trim()) e.surname = 'Required';
    if (!isJambId) {
      if (!session)  e.session  = 'Required';
      if (!semester) e.semester = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true); setErr(''); setData(null); setCourse('');
    try {
      const qs = isJambId ? '' : `?${new URLSearchParams({ session, semester })}`;
      const { data: res } = await api.get(`/verify/${encodeURIComponent(studentId.trim().toUpperCase())}${qs}`);
      const stored = (res.student?.lastName || '').toLowerCase();
      if (stored !== surname.trim().toLowerCase()) {
        setErr('Surname does not match this Student ID.'); return;
      }
      setData(res);
    } catch (ex) {
      setErr(ex.response?.data?.message || 'No record found for this Student ID.');
    } finally { setLoading(false); }
  };

  const allCourses = data ? [...new Set([
    ...(data.quizScores || []).map(s => s.course),
    ...(data.assessmentResults || []).map(r => r.course),
    ...(data.certificates || []).map(c => c.course),
  ])] : [];

  const quizScores        = course ? (data?.quizScores || []).filter(s => s.course === course) : (data?.quizScores || []);
  const assessmentResults = course ? (data?.assessmentResults || []).filter(r => r.course === course) : (data?.assessmentResults || []);
  const certificates      = course ? (data?.certificates || []).filter(c => c.course === course) : (data?.certificates || []);
  const hasResults        = quizScores.length > 0 || assessmentResults.length > 0 || certificates.length > 0;

  const initials = data?.student
    ? `${data.student.firstName?.[0] || ''}${data.student.lastName?.[0] || ''}`.toUpperCase()
    : '';

  const totalPct = quizScores.length
    ? Math.round(quizScores.reduce((s, q) => s + (q.avgAccuracy || 0), 0) / quizScores.length)
    : null;

  const IS = { width: '100%', padding: '10px 13px', borderRadius: 9, border: '1.5px solid #E2E8F0', fontSize: 13, background: '#F8FAFC', color: '#0F172A', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' };
  const onFocus = e => { e.target.style.borderColor = '#FFA500'; e.target.style.boxShadow = '0 0 0 3px rgba(255,165,0,0.1)'; };
  const onBlur  = (e, hasErr) => { e.target.style.borderColor = hasErr ? '#fca5a5' : '#E2E8F0'; e.target.style.boxShadow = 'none'; };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-lg" style={{ padding: 0, overflow: 'hidden', maxHeight: '94vh', display: 'flex', flexDirection: 'column', borderRadius: 20 }}>

        {/* Header */}
        <div style={{ background: '#0f172a', padding: '22px 24px 20px', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                <div style={{ width: 6, height: 6, background: '#FF6A00', borderRadius: '50%' }} />
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.2em', color: '#FFA500', textTransform: 'uppercase' }}>Result Verification Portal</span>
              </div>
              <div style={{ fontFamily: 'var(--display)', fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: '-0.02em' }}>Check Your Result</div>
              {!data && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>Enter your Student ID to view your academic record</div>}
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 9, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}>
              <IcX size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 22px 24px' }}>

          {/* ── Search form ── */}
          {!data && (
            <form onSubmit={handleSubmit} noValidate>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', display: 'block', marginBottom: 5 }}>
                    Student ID <span style={{ color: '#FFA500' }}>*</span>
                  </label>
                  <input value={studentId}
                    onChange={e => { setStudentId(e.target.value); if (errors.studentId) setErrors(p => ({ ...p, studentId: '' })); }}
                    placeholder={idPlaceholder} autoComplete="off"
                    style={{ ...IS, fontFamily: 'monospace, monospace', borderColor: errors.studentId ? '#fca5a5' : '#E2E8F0' }}
                    onFocus={onFocus} onBlur={e => onBlur(e, errors.studentId)} />
                  {errors.studentId && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 3 }}>{errors.studentId}</div>}
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', display: 'block', marginBottom: 5 }}>
                    Surname <span style={{ color: '#FFA500' }}>*</span>
                  </label>
                  <input value={surname}
                    onChange={e => { setSurname(e.target.value); if (errors.surname) setErrors(p => ({ ...p, surname: '' })); }}
                    placeholder="Last name" autoComplete="family-name"
                    style={{ ...IS, borderColor: errors.surname ? '#fca5a5' : '#E2E8F0' }}
                    onFocus={onFocus} onBlur={e => onBlur(e, errors.surname)} />
                  {errors.surname && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 3 }}>{errors.surname}</div>}
                </div>
              </div>
              {isJambId ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 9, padding: '10px 13px', marginBottom: 18 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <span style={{ fontSize: 11.5, color: '#1E40AF', lineHeight: 1.5 }}>JAMB / PUTME record detected — no session or semester needed.</span>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 18 }}>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', display: 'block', marginBottom: 5 }}>
                      Academic Session <span style={{ color: '#FFA500' }}>*</span>
                    </label>
                    <select value={session} onChange={e => setSession(e.target.value)} style={IS} onFocus={onFocus} onBlur={e => onBlur(e, false)}>
                      {SESSIONS.map(s => <option key={s} value={s}>{s} Session</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', display: 'block', marginBottom: 5 }}>
                      Semester <span style={{ color: '#FFA500' }}>*</span>
                    </label>
                    <select value={semester} onChange={e => setSemester(e.target.value)} style={IS} onFocus={onFocus} onBlur={e => onBlur(e, false)}>
                      {SEMESTERS.map(s => <option key={s} value={s}>{s} Semester</option>)}
                    </select>
                  </div>
                </div>
              )}

              {err && (
                <div style={{ display: 'flex', gap: 8, background: '#FFF2F2', border: '1px solid #FECDD3', borderRadius: 9, padding: '10px 14px', marginBottom: 14 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  <span style={{ fontSize: 12, color: '#991B1B', lineHeight: 1.5 }}>{err}</span>
                </div>
              )}

              <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', background: loading ? '#94A3B8' : '#0F172A', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: loading ? 'none' : '0 4px 16px rgba(15,23,42,0.2)' }}>
                {loading
                  ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Checking…</>
                  : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>Check My Result</>}
              </button>

              <div style={{ marginTop: 14, padding: '11px 13px', background: '#F8FAFC', borderRadius: 9, border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Your ID looks like</div>
                <span style={{ fontFamily: 'monospace, monospace', fontSize: 14, fontWeight: 800, color: '#FFA500', letterSpacing: 1.5 }}>
                  {isJambId ? `TPJ / ${CUR_YEAR} / A0001` : `TT / ${enrollYear} / A0001`}
                </span>
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                  University students use <strong>TT/…</strong>; JAMB &amp; PUTME students use <strong>TPJ/…</strong>. Find it on your confirmation email.
                </div>
              </div>
            </form>
          )}

          {/* ── Result card ── */}
          {data && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Back + filters */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => { setData(null); setErr(''); setCourse(''); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  New Search
                </button>
                {allCourses.length > 1 && allCourses.map(c => (
                  <button key={c} onClick={() => setCourse(course === c ? '' : c)} style={{ padding: '5px 11px', borderRadius: 99, border: `1.5px solid ${course === c ? '#FFA500' : '#E2E8F0'}`, background: course === c ? '#FFA500' : '#fff', color: course === c ? '#fff' : '#374151', fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit' }}>{c}</button>
                ))}
              </div>

              {/* Printable card */}
              <div id="rcm-result-card" ref={cardRef} style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', overflow: 'hidden' }}>

                {/* Student header */}
                <div style={{ background: '#0f172a', padding: '20px 20px 0', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -30, right: -20, width: 140, height: 140, background: 'radial-gradient(circle, rgba(255,165,0,0.08), transparent 70%)', pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #FFA500, #ff8c00, transparent)' }} />

                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingBottom: 20, position: 'relative' }}>
                    <div style={{ width: 54, height: 54, borderRadius: '50%', border: '2.5px solid #FFA500', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#FFA500', flexShrink: 0, overflow: 'hidden', fontFamily: 'var(--display)' }}>
                      {data.student?.profilePicture
                        ? <img src={data.student.profilePicture} alt={initials} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : initials || '?'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.22em', color: '#FFA500', textTransform: 'uppercase', marginBottom: 5 }}>Academic Record</div>
                      <div style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 900, color: '#fff', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {data.student?.lastName} {data.student?.firstName}
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {data.student?.department}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: 1 }}>{data.student?.tutorialId}</div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(22,163,74,0.15)', border: '1px solid rgba(22,163,74,0.28)', borderRadius: 99, padding: '2px 8px', marginTop: 5 }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                        <span style={{ fontSize: 9, color: '#4ade80', fontWeight: 700 }}>Verified</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Context strip — academic for students, programme for JAMB */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: '#FAFBFC', borderBottom: '1px solid #F1F5F9' }}>
                  {(data.ecosystem === 'jamb'
                    ? [['Programme', PROGRAM_LABELS[data.jamb?.programType] || 'JAMB'], ['Exam Year', data.jamb?.examYear || '—'], ['University', data.student?.faculty || '—']]
                    : [['Session', session], ['Semester', `${semester} Semester`], ['Faculty', data.student?.faculty || '—']]
                  ).map(([l, v], i, arr) => (
                    <div key={l} style={{ padding: '11px 14px', borderRight: i < arr.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                      <div style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94A3B8', marginBottom: 3 }}>{l}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</div>
                    </div>
                  ))}
                </div>

                {/* Overall score ring */}
                {totalPct !== null && (
                  <div style={{ padding: '14px 18px', background: '#F8FAFC', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                      <svg width="56" height="56" viewBox="0 0 56 56" style={{ transform: 'rotate(-90deg)', display: 'block' }}>
                        <circle cx="28" cy="28" r="22" fill="none" stroke="#EAECF0" strokeWidth="6" />
                        <circle cx="28" cy="28" r="22" fill="none" stroke={scoreColor(totalPct)} strokeWidth="6"
                          strokeDasharray={`${2 * Math.PI * 22}`}
                          strokeDashoffset={`${2 * Math.PI * 22 * (1 - totalPct / 100)}`}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 1s ease' }} />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: scoreColor(totalPct) }}>{totalPct}%</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>{scoreLabel(totalPct)}</div>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                        {data.ecosystem === 'jamb'
                          ? `Overall across ${quizScores.length} subject${quizScores.length !== 1 ? 's' : ''}${data.jamb?.bestMock ? ` · best mock ${data.jamb.bestMock}/400` : ''}`
                          : `Overall across ${quizScores.length} course${quizScores.length !== 1 ? 's' : ''} · ${semester} Semester ${session}`}
                      </div>
                    </div>
                  </div>
                )}

                {/* Results */}
                <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {!hasResults && (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#94A3B8' }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" style={{ display: 'block', margin: '0 auto 10px' }}><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#64748B' }}>
                        {data.ecosystem === 'jamb' ? 'No results recorded yet' : `No results for ${semester} Semester ${session}`}
                      </div>
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 5 }}>
                        {data.ecosystem === 'jamb' ? 'Take a practice quiz or complete a released CA to see results here.' : 'Try a different session or semester.'}
                      </div>
                    </div>
                  )}

                  {/* Quiz Performance */}
                  {quizScores.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(255,165,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFA500" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0F172A' }}>{data.ecosystem === 'jamb' ? 'Subject Performance' : 'Quiz Performance'}</div>
                        <div style={{ fontSize: 10, color: '#94A3B8', marginLeft: 2 }}>{quizScores.length} {data.ecosystem === 'jamb' ? 'subject' : 'course'}{quizScores.length !== 1 ? 's' : ''}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {quizScores.map((s, i) => {
                          const pct = s.avgAccuracy || 0;
                          return (
                            <div key={s.course} style={{ background: '#F8FAFC', borderRadius: 10, padding: '12px 14px', border: '1px solid #F1F5F9' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{s.course}</div>
                                  <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{s.attempts} attempt{s.attempts !== 1 ? 's' : ''} · {scoreLabel(pct)}</div>
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 900, color: scoreColor(pct) }}>{pct}%</div>
                              </div>
                              <AnimBar pct={pct} delay={i * 80} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* CA Results */}
                  {assessmentResults.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0F172A' }}>CA Results</div>
                        <div style={{ fontSize: 10, color: '#94A3B8' }}>{assessmentResults.length} assessment{assessmentResults.length !== 1 ? 's' : ''}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {assessmentResults.map((r, i) => {
                          const pct = parseFloat(r.percentage) || 0;
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>{r.week || 'Assessment'}</div>
                                <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 1 }}>{r.course}</div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: 16, fontWeight: 800, color: scoreColor(pct) }}>{pct}%</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Certificates */}
                  {certificates.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(22,163,74,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0F172A' }}>Certificates Earned</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {certificates.map((c, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'linear-gradient(135deg,#F0FDF4,#DCFCE7)', borderRadius: 10, border: '1px solid #BBF7D0' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#14532D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.course || c.title}</div>
                              <div style={{ fontSize: 10, color: '#16a34a', marginTop: 2 }}>{c.semester} Semester · {c.year}</div>
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#16a34a', flexShrink: 0 }}>{c.grade}%</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Card footer */}
                <div style={{ background: '#F8FAFC', borderTop: '1px solid #F1F5F9', padding: '10px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ fontSize: 10, color: '#94A3B8' }}>Toppers Tutorial · University of Ibadan</span>
                  <span style={{ fontSize: 10, color: '#94A3B8' }}>Verified {new Date().toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>

              {/* Download buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button onClick={() => printCard('rcm-result-card')} style={{ padding: '11px 14px', background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'border-color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#0F172A'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#E2E8F0'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  Save as PDF
                </button>
                <button onClick={() => {
                    const isJ = data.ecosystem === 'jamb';
                    const dlSession = isJ
                      ? `${PROGRAM_LABELS[data.jamb?.programType] || 'JAMB'}${data.jamb?.examYear ? ' ' + data.jamb.examYear : ''}`
                      : session;
                    downloadImage(data, quizScores, assessmentResults, certificates, dlSession, isJ ? '' : semester);
                  }} style={{ padding: '11px 14px', background: '#0F172A', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, boxShadow: '0 4px 14px rgba(15,23,42,0.2)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download Image
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
